import tvgConf from "@tvg/conf";
import {
  GetAlertsResponse,
  ChannelType,
  Subscription,
  ChannelUFC,
  EntityType
} from "@tvg/ts-types/Alerts";
import { FavoriteTopTracksSuccessResponse } from "@tvg/ts-types/Track";
import getProtocol from "../protocolSetter";
import requester from "../requester";
import { serviceResponse } from "../types";

const ufcService: string = "service.ufc";
const getUfcServiceUrl = (): string => `${tvgConf().config(ufcService)}`;

const getUserFavoriteTracks = (accountId: number): Promise<serviceResponse> =>
  requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/tracks`,
    method: "get",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });

const setUserFavoriteTrack = (
  accountId: number,
  trackCode: string
): Promise<serviceResponse> =>
  requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/tracks/${trackCode}`,
    method: "post",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });

const deleteUserFavoriteTrack = (
  accountId: number,
  trackCode: string
): Promise<serviceResponse> =>
  requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/tracks/${trackCode}`,
    method: "delete",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });

const getUserAlerts = (
  accountId: string,
  entityType: string = "race",
  page?: number,
  results?: number
): Promise<{ data: GetAlertsResponse; status: number }> => {
  const params: Record<string, string | number> = { entityTypes: entityType };
  if (page) params.page = page;
  if (results) params.results = results;

  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "get",
    params,
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });
};

const deleteUserAlert = (
  accountId: string,
  ids: Array<number>
): Promise<{ status: number }> => {
  const idsString = ids
    .reduce((currentValue, accumulator) => `${currentValue}${accumulator},`, "")
    .slice(0, -1);

  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "delete",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() },
    params: { ids: idsString }
  });
};

const addEntityAlerts = (accountId: string, entities: ChannelUFC[]) => {
  const newAlerts = entities
    .filter(({ subscriptions }) => subscriptions?.length)
    .map(
      ({
        entityRunnerId,
        entityDob,
        entityName,
        entityType,
        subscriptions
      }) => ({
        entityName,
        entityType,
        ...(entityType === EntityType.Runner
          ? { entityRunnerId, entityDob }
          : {}),
        subscriptions
      })
    );

  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "post",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() },
    data: newAlerts
  });
};

const setUserAlerts = (
  accountId: string,
  entityNames: string[],
  entityType: string = "race",
  eventTypes: string[] = ["raceResults"],
  channelType: ChannelType = ChannelType.PushNotification
): Promise<{ status: number }> => {
  const subscriptions = eventTypes.map((subscription) => ({
    eventType: subscription,
    channels: [
      {
        type: channelType
      }
    ]
  }));

  const newAlerts = entityNames.map((entityName) => ({
    entityType,
    entityName,
    subscriptions
  }));

  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "post",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() },
    data: newAlerts
  });
};

export const updateUserAlerts = (
  accountId: string,
  updates: Array<{
    favoriteId: number;
    subscriptions: Array<Subscription>;
  }>
): Promise<{ status: number }> =>
  requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "put",
    data: updates,
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });

export const addFavorite = (
  accountId: string,
  entityType: string,
  entityNames: string[]
): Promise<FavoriteTopTracksSuccessResponse> => {
  const newFavorites = entityNames.map((entityName) => ({
    accountId,
    entityType,
    entityName
  }));
  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "post",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() },
    data: newFavorites
  });
};

const removeFavorite = (
  accountId: string,
  favoriteId: number | null
): Promise<{ status: number }> =>
  requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites/${favoriteId}`,
    method: "delete",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() }
  });

const addRunnerToStable = (
  accountId: string,
  entityType: string,
  entityIds: string[],
  entityDob: number,
  entityName: string,
  eventTypes: string[] = ["raceResults", "raceDay", "timeToRace"]
): Promise<FavoriteTopTracksSuccessResponse> => {
  const subscriptions = eventTypes.map((subscription) => ({
    eventType: subscription,
    channels: [
      {
        type: "email"
      }
    ]
  }));

  const newFavorites = entityIds.map((entityRunnerId) => ({
    entityType,
    entityRunnerId,
    entityName,
    entityDob,
    subscriptions
  }));
  return requester()({
    url: `${getProtocol()}${getUfcServiceUrl()}/users/${accountId}/favorites`,
    method: "post",
    withCredentials: true,
    headers: { "x-tvg-context": tvgConf().context() },
    data: newFavorites
  });
};

const UFC_SERVICE = {
  getUserFavoriteTracks,
  setUserFavoriteTrack,
  deleteUserFavoriteTrack,
  getUserAlerts,
  deleteUserAlert,
  setUserAlerts,
  updateUserAlerts,
  addFavorite,
  removeFavorite,
  addRunnerToStable,
  addEntityAlerts
};

export default UFC_SERVICE;
